import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';

import CloseIcon from '@mui/icons-material/CloseRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import CreditCardIcon from '@mui/icons-material/CreditCardRounded';

import api from '../../services/api';

export default function PayPerUseModal({ open, onClose, onActivated, isPro, videoContext, hasStripeCustomer = true, hasRealPaymentMethod = true }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const isVideo = !!videoContext;
    // Needs full payment update (no Stripe customer at all)
    const needsPaymentUpdate = isPro && !isVideo && hasStripeCustomer === false;
    // Legacy PRO user: has plan but no real card on file — needs to register payment method
    const needsPaymentSetup = isPro && !isVideo && hasStripeCustomer && !hasRealPaymentMethod;

    const VIDEO_CONTEXT_COPY = {
        pro_light_no_video: {
            title: t('payPerUse.videoUnavailable'),
            subtitle: t('payPerUse.videoUnavailableSub'),
        },
        pro_max_limit_exceeded: {
            title: t('payPerUse.videoLimitExceeded'),
            subtitle: t('payPerUse.videoLimitExceededSub'),
        },
    };
    const videoCopy = VIDEO_CONTEXT_COPY[videoContext] || {};

    const PERKS = [
        t('payPerUse.perks.continue'),
        t('payPerUse.perks.charge'),
        t('payPerUse.perks.noLimit'),
        t('payPerUse.perks.deactivate'),
    ];
    const UPDATE_PAYMENT_PERKS = [
        t('payPerUse.perks.addCard'),
        t('payPerUse.perks.activateCredits'),
        t('payPerUse.perks.charge'),
        t('payPerUse.perks.deactivate'),
    ];
    const VIDEO_PERKS = [
        t('payPerUse.perks.generateVideo'),
        t('payPerUse.perks.charge'),
        t('payPerUse.perks.videoQuality'),
        t('payPerUse.perks.deactivate'),
    ];

    const SETUP_PERKS = [
        t('payPerUse.perks.addCard'),
        t('payPerUse.perks.zeroToday'),
        t('payPerUse.perks.charge'),
        t('payPerUse.perks.deactivate'),
    ];

    const perks = isVideo ? VIDEO_PERKS : needsPaymentUpdate ? UPDATE_PAYMENT_PERKS : needsPaymentSetup ? SETUP_PERKS : PERKS;

    const title = isVideo
        ? (videoCopy.title || t('payPerUse.videoUnavailable'))
        : needsPaymentUpdate
            ? t('payPerUse.paymentRequired')
            : needsPaymentSetup
                ? t('payPerUse.setupRequired')
                : t('payPerUse.exhausted');

    const subtitle = isVideo
        ? (videoCopy.subtitle || t('payPerUse.videoUnavailableSub'))
        : needsPaymentUpdate
            ? t('payPerUse.paymentRequiredSub')
            : needsPaymentSetup
                ? t('payPerUse.setupRequiredSub')
                : t('payPerUse.exhaustedSub');

    const handleActivate = async () => {
        if (!isPro) return;
        setLoading(true);
        setError('');
        try {
            const res = await api.post('/billing/stripe/pay-per-use/', { enable: true });
            if (res.data?.pay_per_use_enabled) {
                onActivated && onActivated();
                onClose();
            } else {
                setError(res.data?.error || t('payPerUse.exhaustedSub'));
            }
        } catch (e) {
            setError(e?.response?.data?.error || 'Errore di connessione');
        } finally {
            setLoading(false);
        }
    };

    const handleSetupPayment = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.post('/billing/stripe/pay-per-use-setup/');
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else if (res.data?.already_setup || res.data?.dev_mode) {
                onActivated && onActivated();
                onClose();
            } else {
                setError(res.data?.error || 'Errore durante la configurazione');
            }
        } catch (e) {
            setError(e?.response?.data?.error || 'Errore di connessione');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePaymentMethod = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.post('/billing/stripe/portal/');
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                const checkoutRes = await api.post('/billing/stripe/checkout/', { plan: 'pro_light' });
                if (checkoutRes.data?.url) {
                    window.location.href = checkoutRes.data.url;
                } else if (checkoutRes.data?.dev_mode) {
                    onActivated && onActivated();
                    onClose();
                } else {
                    setError(checkoutRes.data?.error || 'Errore durante la procedura');
                }
            }
        } catch (e) {
            setError(e?.response?.data?.error || 'Errore di connessione');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    background: '#fff',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.13)',
                    overflow: 'hidden',
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                {/* Header */}
                <Box sx={{
                    px: 3,
                    pt: 3,
                    pb: 2.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid rgba(0,0,0,0.07)',
                }}>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#18181B', mb: 0.3 }}>
                            {title}
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: '#6B6B7B' }}>
                            {subtitle}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ color: '#9B9BA8', mt: -0.5, '&:hover': { color: '#18181B', background: 'rgba(0,0,0,0.06)' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ p: 3 }}>
                    {/* How it works */}
                    <Box sx={{ background: '#F7F7F5', borderRadius: '12px', p: 2, mb: 2.5 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#9B9BA8', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.8 }}>
                            {t('payPerUse.howItWorks')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#3B3B4B', lineHeight: 1.6 }}>
                            {needsPaymentUpdate
                                ? <>{t('payPerUse.bodyPayment').split('<1>')[0]}<strong>{t('payPerUse.bodyPayment').split('<1>')[1]?.split('</1>')[0]}</strong>{t('payPerUse.bodyPayment').split('</1>')[1]}</>
                                : needsPaymentSetup
                                    ? <>{t('payPerUse.bodySetup').split('<1>')[0]}<strong>{t('payPerUse.bodySetup').split('<1>')[1]?.split('</1>')[0]}</strong>{t('payPerUse.bodySetup').split('</1>')[1]}</>
                                    : <>{t('payPerUse.bodyNormal').split('<1>')[0]}<strong>{t('payPerUse.bodyNormal').split('<1>')[1]?.split('</1>')[0]}</strong>{t('payPerUse.bodyNormal').split('</1>')[1]}</>
                            }
                        </Typography>
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(0,0,0,0.07)', mb: 2 }} />

                    {perks.map((p, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.9 }}>
                            <CheckIcon sx={{ fontSize: '0.9rem', color: '#701280', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.83rem', color: '#5B5B6B' }}>{p}</Typography>
                        </Box>
                    ))}

                    {!isPro && (
                        <Box sx={{ mt: 2, background: '#FFF3F3', border: '1px solid #FFD5D5', borderRadius: '10px', p: 1.8 }}>
                            <Typography sx={{ fontSize: '0.82rem', color: '#DC2626', fontWeight: 600 }}>
                                {t('payPerUse.proOnly')}
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Typography sx={{ mt: 1.5, color: '#EF4444', fontSize: '0.82rem', textAlign: 'center' }}>{error}</Typography>
                    )}

                    {needsPaymentUpdate ? (
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            onClick={handleUpdatePaymentMethod}
                            startIcon={!loading && <CreditCardIcon />}
                            sx={{
                                mt: 2.5,
                                background: 'linear-gradient(135deg, #701280, #9C3AAE)',
                                borderRadius: '11px',
                                fontWeight: 700,
                                fontSize: '0.88rem',
                                py: 1.3,
                                boxShadow: 'none',
                                '&:hover': { background: 'linear-gradient(135deg, #5A0F68, #7A2890)', boxShadow: '0 4px 16px rgba(112,18,128,0.3)' },
                                '&:disabled': { background: 'rgba(0,0,0,0.09)', boxShadow: 'none' },
                            }}
                        >
                            {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('payPerUse.updatePayment')}
                        </Button>
                    ) : needsPaymentSetup ? (
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            onClick={handleSetupPayment}
                            startIcon={!loading && <CreditCardIcon />}
                            sx={{
                                mt: 2.5,
                                background: 'linear-gradient(135deg, #701280, #9C3AAE)',
                                borderRadius: '11px',
                                fontWeight: 700,
                                fontSize: '0.88rem',
                                py: 1.3,
                                boxShadow: 'none',
                                '&:hover': { background: 'linear-gradient(135deg, #5A0F68, #7A2890)', boxShadow: '0 4px 16px rgba(112,18,128,0.3)' },
                                '&:disabled': { background: 'rgba(0,0,0,0.09)', boxShadow: 'none' },
                            }}
                        >
                            {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('payPerUse.setupPayment')}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={loading || !isPro}
                            onClick={handleActivate}
                            sx={{
                                mt: 2.5,
                                background: '#18181B',
                                borderRadius: '11px',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                py: 1.3,
                                boxShadow: 'none',
                                '&:hover': { background: '#27272A', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' },
                                '&:disabled': { background: 'rgba(0,0,0,0.09)', boxShadow: 'none' },
                            }}
                        >
                            {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('payPerUse.activate')}
                        </Button>
                    )}

                    <Typography sx={{ fontSize: '0.7rem', color: '#BABAC8', textAlign: 'center', mt: 1.2 }}>
                        {t('payPerUse.securePayment')}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
